import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import {CustomsClearanceServiceVariant} from "./models/customs-clearance-service-variant.model";
import {CustomsClearanceHeader} from "./models/customs-clearance-header.model";
import {Page} from "../models/page.model";
import {Shipper} from "./models/shipper.model";
import {environment} from "../../environments/environment";
import {CustomsClearanceService} from "./models/customs-clearance-service.model";
import {CustomsClearanceFile} from "./models/customs-clearance-file.model";
import {CustomsClearanceContacts} from "./models/customs-clearance-contact.model";
import { CustomsDeclaration } from './models/customs-declaration.model';
import { CustomsDeclarationItem } from './models/customs-declaration-item.model';
import { CustomsComplianceDocument } from './models/customs-compliance-document.model';
import { CustomsBrokerAuthorization } from './models/customs-broker-authorization.model';
import { CustomsActivityLog } from './models/customs-activity-log.model';
import { CustomsClearanceRelatedResource } from './models/customs-clearance-related-resource.model';
import { ClearanceReadiness } from './models/clearance-readiness.model';
import { CustomsDutyTaxAssessment } from './models/customs-duty-tax-assessment.model';
import { CustomsPaymentReceipt } from './models/customs-payment-receipt.model';
import { CustomsContainer } from './models/customs-container.model';
import { ClearanceFinancialSummary } from './models/clearance-financial-summary.model';
import { ClearanceExecutionReadiness } from './models/clearance-execution-readiness.model';
import { CustomsIntegrationStatus } from './models/customs-integration-status.model';
import { CustomsIntegrationEvent } from './models/customs-integration-event.model';
import { ClearanceIntegrationSummary } from './models/clearance-integration-summary.model';
import { CustomsProcedureCase } from './models/customs-procedure-case.model';
import { CustomsGuaranteeDeposit } from './models/customs-guarantee-deposit.model';
import { CustomsProcedureClosureEvent } from './models/customs-procedure-closure-event.model';
import { ClearanceAccountingLink } from './models/clearance-accounting-link.model';
import { ClearanceModuleLinks } from './models/clearance-module-links.model';
import { ClearanceWorkspaceSummary } from './models/clearance-workspace-summary.model';

class CustomsClearanceDetail {
}

@Injectable({
    providedIn: 'root'
})
export class CustomsClearanceEndpointService {

    private baseUrlServices = `${environment.restApiUrl}customs-clearance`;
    private baseUrlTransactions = `${environment.restApiUrl}customs-clearance-transactions`;
    private baseUrlShippers = `${environment.restApiUrl}shippers`;
    private baseUrlFiles = `${environment.restApiUrl}customs-clearance-transactions/files`;

    constructor(private http: HttpClient) { }

    // CustomsClearanceService Endpoints
    getAllCustomsClearanceServices(): Observable<CustomsClearanceService[]> {
        return this.http.get<CustomsClearanceService[]>(`${this.baseUrlServices}/services`);
    }

    getCustomsClearanceServiceById(id: number): Observable<CustomsClearanceService> {
        return this.http.get<CustomsClearanceService>(`${this.baseUrlServices}/services/${id}`);
    }

    createCustomsClearanceService(service: CustomsClearanceService): Observable<CustomsClearanceService> {
        return this.http.post<CustomsClearanceService>(`${this.baseUrlServices}/services`, service);
    }

    updateCustomsClearanceService(id: number, service: CustomsClearanceService): Observable<CustomsClearanceService> {
        return this.http.put<CustomsClearanceService>(`${this.baseUrlServices}/services/${id}`, service);
    }

    deleteCustomsClearanceService(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlServices}/services/${id}`);
    }

    // CustomsClearanceServiceVariant Endpoints
    getAllCustomsClearanceServiceVariants(): Observable<CustomsClearanceServiceVariant[]> {
        return this.http.get<CustomsClearanceServiceVariant[]>(`${this.baseUrlServices}/variants`);
    }

    getCustomsClearanceServiceVariantById(id: number): Observable<CustomsClearanceServiceVariant> {
        return this.http.get<CustomsClearanceServiceVariant>(`${this.baseUrlServices}/variants/${id}`);
    }

    createCustomsClearanceServiceVariant(variant: CustomsClearanceServiceVariant): Observable<CustomsClearanceServiceVariant> {
        return this.http.post<CustomsClearanceServiceVariant>(`${this.baseUrlServices}/variants`, variant);
    }

    updateCustomsClearanceServiceVariant(id: number, variant: CustomsClearanceServiceVariant): Observable<CustomsClearanceServiceVariant> {
        return this.http.put<CustomsClearanceServiceVariant>(`${this.baseUrlServices}/variants/${id}`, variant);
    }

    deleteCustomsClearanceServiceVariant(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlServices}/variants/${id}`);
    }

    // CustomsClearanceHeader Endpoints
    getAllCustomsClearanceHeadersByCompany(
        owningCompanyId: number,
        page: number,
        size: number,
        sortBy: string,
        sortDirection: string,
        logisticsProjectId?: number | null
    ): Observable<Page<CustomsClearanceHeader>> {
        let params = new HttpParams()
            .set('page', String(page))
            .set('size', String(size))
            .set('sortBy', sortBy)
            .set('sortDirection', sortDirection);
        if (logisticsProjectId) {
            params = params.set('logisticsProjectId', logisticsProjectId);
        }
        return this.http.get<Page<CustomsClearanceHeader>>(`${this.baseUrlTransactions}/headers/company/${owningCompanyId}`, { params });
    }

    getAllCustomsClearanceHeaders(): Observable<CustomsClearanceHeader[]> {
        return this.http.get<CustomsClearanceHeader[]>(`${this.baseUrlTransactions}/headers`);
    }

    getCustomsClearanceHeaderById(id: number): Observable<CustomsClearanceHeader> {
        return this.http.get<CustomsClearanceHeader>(`${this.baseUrlTransactions}/headers/${id}`);
    }

    createCustomsClearanceHeader(header: CustomsClearanceHeader): Observable<CustomsClearanceHeader> {
        return this.http.post<CustomsClearanceHeader>(`${this.baseUrlTransactions}/headers`, header);
    }

    updateCustomsClearanceHeader(id: number, header: CustomsClearanceHeader): Observable<CustomsClearanceHeader> {
        return this.http.put<CustomsClearanceHeader>(`${this.baseUrlTransactions}/headers/${id}`, header);
    }

    deleteCustomsClearanceHeader(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/headers/${id}`);
    }

    // CustomsClearanceDetail Endpoints
    getAllCustomsClearanceDetails(): Observable<CustomsClearanceDetail[]> {
        return this.http.get<CustomsClearanceDetail[]>(`${this.baseUrlTransactions}/details`);
    }

    getCustomsClearanceDetailById(id: number): Observable<CustomsClearanceDetail> {
        return this.http.get<CustomsClearanceDetail>(`${this.baseUrlTransactions}/details/${id}`);
    }

    createCustomsClearanceDetail(headerId: number, detail: CustomsClearanceDetail): Observable<CustomsClearanceDetail> {
        return this.http.post<CustomsClearanceDetail>(`${this.baseUrlTransactions}/details/header/${headerId}`, detail);
    }

    updateCustomsClearanceDetail(headerId: number,id: number, detail: CustomsClearanceDetail): Observable<CustomsClearanceDetail> {
        return this.http.put<CustomsClearanceDetail>(`${this.baseUrlTransactions}/details/${id}/header/${headerId}`, detail);
    }

    deleteCustomsClearanceDetail(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/details/${id}`);
    }

    // Shipper Endpoints
    getAllShippersByCompany(relatedCompanyId: number, page: number, size: number, sortBy: string, sortDirection: string): Observable<Page<Shipper>> {
        return this.http.get<Page<Shipper>>(`${this.baseUrlShippers}/company/${relatedCompanyId}?page=${page}&size=${size}&sortBy=${sortBy}&sortDirection=${sortDirection}`);
    }

    getAllShippers(): Observable<Shipper[]> {
        return this.http.get<Shipper[]>(`${this.baseUrlShippers}`);
    }

    getShipperById(id: number): Observable<Shipper> {
        return this.http.get<Shipper>(`${this.baseUrlShippers}/${id}`);
    }

    createShipper(shipper: Shipper): Observable<Shipper> {
        return this.http.post<Shipper>(`${this.baseUrlShippers}`, this.toShipperRequest(shipper));
    }

    updateShipper(id: number, shipper: Shipper): Observable<Shipper> {
        return this.http.put<Shipper>(`${this.baseUrlShippers}/${id}`, this.toShipperRequest(shipper));
    }

    deleteShipper(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlShippers}/${id}`);
    }

    createCustomsClearanceFile(customsClearanceFile: CustomsClearanceFile): Observable<CustomsClearanceFile> {
        return this.http.post<CustomsClearanceFile>(this.baseUrlFiles, customsClearanceFile);
    }

    updateCustomsClearanceFile(id: number, customsClearanceFile: CustomsClearanceFile): Observable<CustomsClearanceFile> {
        return this.http.put<CustomsClearanceFile>(`${this.baseUrlFiles}/${id}`, customsClearanceFile);
    }

    deleteCustomsClearanceFile(id: number): Observable<HttpResponse<void>> {
        return this.http.delete<void>(`${this.baseUrlFiles}/${id}`, { observe: 'response' });
    }

    // CustomsClearanceContacts Endpoints
    createCustomsClearanceContacts(contacts: CustomsClearanceContacts): Observable<CustomsClearanceContacts> {
        return this.http.post<CustomsClearanceContacts>(`${this.baseUrlTransactions}/contacts`, contacts);
    }

    updateCustomsClearanceContacts(contacts: CustomsClearanceContacts): Observable<CustomsClearanceContacts> {
        return this.http.put<CustomsClearanceContacts>(`${this.baseUrlTransactions}/contacts`, contacts);
    }

    deleteCustomsClearanceContacts(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/contacts/${id}`);
    }

    getCustomsClearanceContactsByHeaderId(headerId: number): Observable<CustomsClearanceContacts[]> {
        return this.http.get<CustomsClearanceContacts[]>(`${this.baseUrlTransactions}/contacts/header/${headerId}`);
    }

    getDeclarations(headerId: number): Observable<CustomsDeclaration[]> {
        return this.http.get<CustomsDeclaration[]>(`${this.baseUrlTransactions}/headers/${headerId}/declarations`);
    }

    saveDeclaration(headerId: number, declaration: CustomsDeclaration): Observable<CustomsDeclaration> {
        if (declaration.id) {
            return this.http.put<CustomsDeclaration>(`${this.baseUrlTransactions}/headers/${headerId}/declarations/${declaration.id}`, declaration);
        }
        return this.http.post<CustomsDeclaration>(`${this.baseUrlTransactions}/headers/${headerId}/declarations`, declaration);
    }

    deleteDeclaration(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/declarations/${id}`);
    }

    getDeclarationItems(declarationId: number): Observable<CustomsDeclarationItem[]> {
        return this.http.get<CustomsDeclarationItem[]>(`${this.baseUrlTransactions}/declarations/${declarationId}/items`);
    }

    saveDeclarationItem(declarationId: number, item: CustomsDeclarationItem): Observable<CustomsDeclarationItem> {
        if (item.id) {
            return this.http.put<CustomsDeclarationItem>(`${this.baseUrlTransactions}/declarations/${declarationId}/items/${item.id}`, item);
        }
        return this.http.post<CustomsDeclarationItem>(`${this.baseUrlTransactions}/declarations/${declarationId}/items`, item);
    }

    deleteDeclarationItem(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/declaration-items/${id}`);
    }

    getComplianceDocuments(headerId: number): Observable<CustomsComplianceDocument[]> {
        return this.http.get<CustomsComplianceDocument[]>(`${this.baseUrlTransactions}/headers/${headerId}/compliance-documents`);
    }

    saveComplianceDocument(headerId: number, document: CustomsComplianceDocument): Observable<CustomsComplianceDocument> {
        if (document.id) {
            return this.http.put<CustomsComplianceDocument>(`${this.baseUrlTransactions}/headers/${headerId}/compliance-documents/${document.id}`, document);
        }
        return this.http.post<CustomsComplianceDocument>(`${this.baseUrlTransactions}/headers/${headerId}/compliance-documents`, document);
    }

    generateDefaultComplianceDocuments(headerId: number): Observable<CustomsComplianceDocument[]> {
        return this.http.post<CustomsComplianceDocument[]>(`${this.baseUrlTransactions}/headers/${headerId}/compliance-documents/generate`, {});
    }

    deleteComplianceDocument(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/compliance-documents/${id}`);
    }

    getBrokerAuthorizations(headerId: number): Observable<CustomsBrokerAuthorization[]> {
        return this.http.get<CustomsBrokerAuthorization[]>(`${this.baseUrlTransactions}/headers/${headerId}/broker-authorizations`);
    }

    saveBrokerAuthorization(headerId: number, authorization: CustomsBrokerAuthorization): Observable<CustomsBrokerAuthorization> {
        if (authorization.id) {
            return this.http.put<CustomsBrokerAuthorization>(`${this.baseUrlTransactions}/headers/${headerId}/broker-authorizations/${authorization.id}`, authorization);
        }
        return this.http.post<CustomsBrokerAuthorization>(`${this.baseUrlTransactions}/headers/${headerId}/broker-authorizations`, authorization);
    }

    deleteBrokerAuthorization(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/broker-authorizations/${id}`);
    }

    getActivity(headerId: number): Observable<CustomsActivityLog[]> {
        return this.http.get<CustomsActivityLog[]>(`${this.baseUrlTransactions}/headers/${headerId}/activity`);
    }

    saveActivity(headerId: number, activity: CustomsActivityLog): Observable<CustomsActivityLog> {
        return this.http.post<CustomsActivityLog>(`${this.baseUrlTransactions}/headers/${headerId}/activity`, activity);
    }

    getReadiness(headerId: number): Observable<ClearanceReadiness> {
        return this.http.get<ClearanceReadiness>(`${this.baseUrlTransactions}/headers/${headerId}/readiness`);
    }

    getWorkspaceSummary(headerId: number): Observable<ClearanceWorkspaceSummary> {
        return this.http.get<ClearanceWorkspaceSummary>(`${this.baseUrlTransactions}/headers/${headerId}/workspace-summary`);
    }

    getRelatedResources(headerId: number): Observable<CustomsClearanceRelatedResource[]> {
        return this.http.get<CustomsClearanceRelatedResource[]>(`${this.baseUrlTransactions}/headers/${headerId}/related-resources`);
    }

    saveRelatedResource(headerId: number, resource: CustomsClearanceRelatedResource): Observable<CustomsClearanceRelatedResource> {
        if (resource.id) {
            return this.http.put<CustomsClearanceRelatedResource>(`${this.baseUrlTransactions}/headers/${headerId}/related-resources/${resource.id}`, resource);
        }
        return this.http.post<CustomsClearanceRelatedResource>(`${this.baseUrlTransactions}/headers/${headerId}/related-resources`, resource);
    }

    deleteRelatedResource(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/related-resources/${id}`);
    }

    getProcedureCases(headerId: number): Observable<CustomsProcedureCase[]> {
        return this.http.get<CustomsProcedureCase[]>(`${this.baseUrlTransactions}/headers/${headerId}/procedure-cases`);
    }

    saveProcedureCase(headerId: number, procedureCase: CustomsProcedureCase): Observable<CustomsProcedureCase> {
        if (procedureCase.id) {
            return this.http.put<CustomsProcedureCase>(`${this.baseUrlTransactions}/headers/${headerId}/procedure-cases/${procedureCase.id}`, procedureCase);
        }
        return this.http.post<CustomsProcedureCase>(`${this.baseUrlTransactions}/headers/${headerId}/procedure-cases`, procedureCase);
    }

    deleteProcedureCase(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/procedure-cases/${id}`);
    }

    getGuaranteeDeposits(headerId: number): Observable<CustomsGuaranteeDeposit[]> {
        return this.http.get<CustomsGuaranteeDeposit[]>(`${this.baseUrlTransactions}/headers/${headerId}/guarantee-deposits`);
    }

    saveGuaranteeDeposit(headerId: number, guaranteeDeposit: CustomsGuaranteeDeposit): Observable<CustomsGuaranteeDeposit> {
        if (guaranteeDeposit.id) {
            return this.http.put<CustomsGuaranteeDeposit>(`${this.baseUrlTransactions}/headers/${headerId}/guarantee-deposits/${guaranteeDeposit.id}`, guaranteeDeposit);
        }
        return this.http.post<CustomsGuaranteeDeposit>(`${this.baseUrlTransactions}/headers/${headerId}/guarantee-deposits`, guaranteeDeposit);
    }

    deleteGuaranteeDeposit(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/guarantee-deposits/${id}`);
    }

    getProcedureClosureEvents(headerId: number): Observable<CustomsProcedureClosureEvent[]> {
        return this.http.get<CustomsProcedureClosureEvent[]>(`${this.baseUrlTransactions}/headers/${headerId}/procedure-closure-events`);
    }

    saveProcedureClosureEvent(headerId: number, procedureCaseId: number, event: CustomsProcedureClosureEvent): Observable<CustomsProcedureClosureEvent> {
        if (event.id) {
            return this.http.put<CustomsProcedureClosureEvent>(`${this.baseUrlTransactions}/headers/${headerId}/procedure-cases/${procedureCaseId}/closure-events/${event.id}`, event);
        }
        return this.http.post<CustomsProcedureClosureEvent>(`${this.baseUrlTransactions}/headers/${headerId}/procedure-cases/${procedureCaseId}/closure-events`, event);
    }

    deleteProcedureClosureEvent(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/procedure-closure-events/${id}`);
    }

    getAccountingLinks(headerId: number): Observable<ClearanceAccountingLink[]> {
        return this.http.get<ClearanceAccountingLink[]>(`${this.baseUrlTransactions}/headers/${headerId}/accounting-links`);
    }

    getModuleLinks(headerId: number): Observable<ClearanceModuleLinks> {
        return this.http.get<ClearanceModuleLinks>(`${this.baseUrlTransactions}/headers/${headerId}/module-links`);
    }

    linkShipment(headerId: number, shipmentId: number): Observable<ClearanceModuleLinks> {
        return this.http.post<ClearanceModuleLinks>(`${this.baseUrlTransactions}/headers/${headerId}/link-shipment`, { shipmentId });
    }

    generatePayables(headerId: number): Observable<ClearanceAccountingLink[]> {
        return this.http.post<ClearanceAccountingLink[]>(`${this.baseUrlTransactions}/headers/${headerId}/generate-payables`, {});
    }

    syncProductivity(headerId: number): Observable<ClearanceModuleLinks> {
        return this.http.post<ClearanceModuleLinks>(`${this.baseUrlTransactions}/headers/${headerId}/sync-productivity`, {});
    }

    getDutyTaxAssessments(declarationId: number): Observable<CustomsDutyTaxAssessment[]> {
        return this.http.get<CustomsDutyTaxAssessment[]>(`${this.baseUrlTransactions}/declarations/${declarationId}/duty-tax-assessments`);
    }

    saveDutyTaxAssessment(declarationId: number, assessment: CustomsDutyTaxAssessment): Observable<CustomsDutyTaxAssessment> {
        if (assessment.id) {
            return this.http.put<CustomsDutyTaxAssessment>(`${this.baseUrlTransactions}/declarations/${declarationId}/duty-tax-assessments/${assessment.id}`, assessment);
        }
        return this.http.post<CustomsDutyTaxAssessment>(`${this.baseUrlTransactions}/declarations/${declarationId}/duty-tax-assessments`, assessment);
    }

    deleteDutyTaxAssessment(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/duty-tax-assessments/${id}`);
    }

    getPaymentReceipts(headerId: number): Observable<CustomsPaymentReceipt[]> {
        return this.http.get<CustomsPaymentReceipt[]>(`${this.baseUrlTransactions}/headers/${headerId}/payment-receipts`);
    }

    savePaymentReceipt(headerId: number, receipt: CustomsPaymentReceipt): Observable<CustomsPaymentReceipt> {
        if (receipt.id) {
            return this.http.put<CustomsPaymentReceipt>(`${this.baseUrlTransactions}/headers/${headerId}/payment-receipts/${receipt.id}`, receipt);
        }
        return this.http.post<CustomsPaymentReceipt>(`${this.baseUrlTransactions}/headers/${headerId}/payment-receipts`, receipt);
    }

    deletePaymentReceipt(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/payment-receipts/${id}`);
    }

    getCustomsContainers(headerId: number): Observable<CustomsContainer[]> {
        return this.http.get<CustomsContainer[]>(`${this.baseUrlTransactions}/headers/${headerId}/containers`);
    }

    saveCustomsContainer(headerId: number, container: CustomsContainer): Observable<CustomsContainer> {
        if (container.id) {
            return this.http.put<CustomsContainer>(`${this.baseUrlTransactions}/headers/${headerId}/containers/${container.id}`, container);
        }
        return this.http.post<CustomsContainer>(`${this.baseUrlTransactions}/headers/${headerId}/containers`, container);
    }

    generateCustomsContainersFromHeader(headerId: number): Observable<CustomsContainer[]> {
        return this.http.post<CustomsContainer[]>(`${this.baseUrlTransactions}/headers/${headerId}/containers/generate-from-header`, {});
    }

    deleteCustomsContainer(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/containers/${id}`);
    }

    getFinancialSummary(headerId: number): Observable<ClearanceFinancialSummary> {
        return this.http.get<ClearanceFinancialSummary>(`${this.baseUrlTransactions}/headers/${headerId}/financial-summary`);
    }

    getExecutionReadiness(headerId: number): Observable<ClearanceExecutionReadiness> {
        return this.http.get<ClearanceExecutionReadiness>(`${this.baseUrlTransactions}/headers/${headerId}/execution-readiness`);
    }

    getIntegrationStatuses(headerId: number): Observable<CustomsIntegrationStatus[]> {
        return this.http.get<CustomsIntegrationStatus[]>(`${this.baseUrlTransactions}/headers/${headerId}/integration-statuses`);
    }

    saveIntegrationStatus(headerId: number, status: CustomsIntegrationStatus): Observable<CustomsIntegrationStatus> {
        if (status.id) {
            return this.http.put<CustomsIntegrationStatus>(`${this.baseUrlTransactions}/integration-statuses/${status.id}`, status);
        }
        return this.http.post<CustomsIntegrationStatus>(`${this.baseUrlTransactions}/headers/${headerId}/integration-statuses`, status);
    }

    updateManualIntegrationStatus(statusId: number, status: CustomsIntegrationStatus): Observable<CustomsIntegrationStatus> {
        return this.http.post<CustomsIntegrationStatus>(`${this.baseUrlTransactions}/integration-statuses/${statusId}/manual-status`, status);
    }

    refreshIntegrationStatus(statusId: number): Observable<CustomsIntegrationStatus> {
        return this.http.post<CustomsIntegrationStatus>(`${this.baseUrlTransactions}/integration-statuses/${statusId}/refresh`, {});
    }

    deleteIntegrationStatus(statusId: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrlTransactions}/integration-statuses/${statusId}`);
    }

    getIntegrationEvents(headerId: number): Observable<CustomsIntegrationEvent[]> {
        return this.http.get<CustomsIntegrationEvent[]>(`${this.baseUrlTransactions}/headers/${headerId}/integration-events`);
    }

    getIntegrationSummary(headerId: number): Observable<ClearanceIntegrationSummary> {
        return this.http.get<ClearanceIntegrationSummary>(`${this.baseUrlTransactions}/headers/${headerId}/integration-summary`);
    }

    private toShipperRequest(shipper: Shipper): Shipper {
        return {
            shipperID: shipper.shipperID,
            shipperName: shipper.shipperName,
            address: shipper.address,
            contactPerson: shipper.contactPerson,
            contactPhone: shipper.contactPhone,
            contactEmail: shipper.contactEmail,
            createdDate: shipper.createdDate,
            updatedDate: shipper.updatedDate,
            relatedCompanyId: shipper.relatedCompanyId
        } as Shipper;
    }

}
